import Swiper from "./swiper-bundle.esm.browser.min.js";
import { initCartUpsellSubscriptions } from "./oo-cart-upsell-subscription.js";

if (!customElements.get("oo-cart-upsell")) {
  class OoCartUpsell extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector(".oo-cart-upsell__list");
      this.swiperHost = this.querySelector(".oo-cart-upsell__swiper");
      this.prevBtn = this.querySelector(".oo-cart-upsell__nav--prev");
      this.nextBtn = this.querySelector(".oo-cart-upsell__nav--next");
      this.sectionId = this.dataset.sectionId || "cart-drawer";
      this.limit = this.dataset.limit || "4";
      this.intents = (this.dataset.intents || "")
        .split(",")
        .filter(Boolean);
      this.subscriptionsEnabled = this.dataset.subscriptionsEnabled === "true";
      this._lastProductIds = "";
      this.swiper = null;
      this._loadId = 0;

      this.load();

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          const productIds = this.dataset.productIds || "";
          if (productIds !== this._lastProductIds) {
            this.load();
          } else {
            this.filterCartItems();
          }
        });
      }
    }

    disconnectedCallback() {
      this.destroySlider();
      this._unsub?.();
    }

    getRecommendationsUrl() {
      return window.routes?.product_recommendations_url;
    }

    cleanSubscriptionHtml(html) {
      return html
        .replace(/\s*data-oo-upsell-subscription-ready="true"/g, "")
        .replace(/\s*data-oo-upsell-subscription-ready='true'/g, "")
        .replace(/\s*data-oo-upsell-subscription-bound="true"/g, "")
        .replace(/\s*data-oo-upsell-subscription-bound='true'/g, "");
    }

    getSubscriptionItems() {
      const source = document.getElementById("oo-cart-upsell-subscriptions-source");
      if (!source || !this.subscriptionsEnabled) return [];

      return [...source.querySelectorAll(".oo-cart-upsell-item")].map((item) => {
        item.removeAttribute("data-oo-upsell-subscription-bound");
        return this.cleanSubscriptionHtml(item.outerHTML);
      });
    }

    renderSubscriptionItems(htmlItems) {
      if (!htmlItems.length || !this.list) return;

      this.destroySlider();
      this.list.innerHTML = htmlItems.join("");
      initCartUpsellSubscriptions(this.list);
      this.classList.remove("hidden");
      this.initSlider();
    }

    filterCartItems() {
      const cartIds = (this.dataset.productIds || "").split(",").filter(Boolean);
      let visible = 0;

      this.list.querySelectorAll(".oo-cart-upsell-item").forEach((item) => {
        const inCart = cartIds.includes(item.dataset.productId);
        item.classList.toggle("hidden", inCart);
        if (!inCart) visible += 1;
      });

      this.classList.toggle("hidden", visible === 0);
      this.initSlider();
    }

    destroySlider() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.swiperHost?.classList.remove("swiper");
      this.list?.classList.remove("swiper-wrapper");

      this.unwrapSlides();
    }

    unwrapSlides() {
      this.list?.querySelectorAll(".oo-cart-upsell__slide").forEach((slide) => {
        const item = slide.querySelector(".oo-cart-upsell-item");
        if (item) this.list.insertBefore(item, slide);
        slide.remove();
      });
    }

    initSlider() {
      const items = [
        ...this.list.querySelectorAll(".oo-cart-upsell-item:not(.hidden)"),
      ];
      const showNav = items.length > 1;

      this.prevBtn?.classList.toggle("hidden", !showNav);
      this.nextBtn?.classList.toggle("hidden", !showNav);

      const previousIndex = this.swiper?.activeIndex || 0;
      this.destroySlider();

      if (!showNav) return;

      items.forEach((item) => {
        const slide = document.createElement("div");
        slide.className = "oo-cart-upsell__slide swiper-slide";
        this.list.appendChild(slide);
        slide.appendChild(item);
      });

      this.swiperHost.classList.add("swiper");
      this.list.classList.add("swiper-wrapper");

      this.swiper = new Swiper(this.swiperHost, {
        initialSlide: Math.min(previousIndex, items.length - 1),
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 200,
        autoHeight: true,
        allowTouchMove: true,
        navigation: {
          nextEl: this.nextBtn,
          prevEl: this.prevBtn,
        },
      });
    }

    async fetchRecommendationItems(productIds, cartIds, seen, loadId) {
      const items = [];
      const recommendationsUrl = this.getRecommendationsUrl();
      if (!recommendationsUrl || !this.intents.length) return items;

      for (const intent of this.intents) {
        for (const id of productIds) {
          const limit = intent === "related" ? "4" : this.limit;
          const url = `${recommendationsUrl}?section_id=${this.sectionId}&product_id=${id}&intent=${intent}&limit=${limit}`;

          try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(response.status);

            const text = await response.text();
            if (loadId !== this._loadId) return items;

            const doc = new DOMParser().parseFromString(text, "text/html");
            const list =
              doc.querySelector(".oo-cart-upsell-response .oo-cart-upsell__list") ||
              doc.querySelector(
                ".product-recommendations.oo-cart-upsell-response .oo-cart-upsell__list",
              );

            list?.querySelectorAll(".oo-cart-upsell-item").forEach((item) => {
              const pid = item.dataset.productId;
              if (!pid || seen.has(pid) || cartIds.has(pid)) return;
              if (item.dataset.ooUpsellType === "subscription") return;
              seen.add(pid);
              items.push(item.outerHTML);
            });
          } catch (error) {
            console.error("oo-cart-upsell fetch error", error);
          }
        }
      }

      return items;
    }

    async load() {
      const productIds = (this.dataset.productIds || "").split(",").filter(Boolean);
      this._lastProductIds = this.dataset.productIds || "";
      const loadId = ++this._loadId;

      const hasRecommendations = productIds.length > 0 && this.intents.length > 0;
      const subscriptionItems = this.getSubscriptionItems();
      const hasSubscriptions = subscriptionItems.length > 0;

      if (!hasRecommendations && !hasSubscriptions) {
        this.classList.add("hidden");
        this.destroySlider();
        if (this.list) this.list.innerHTML = "";
        return;
      }

      const cartIds = new Set(productIds);
      const seen = new Set();
      const subscriptionHtmlItems = [];

      subscriptionItems.forEach((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const item = doc.querySelector(".oo-cart-upsell-item");
        const pid = item?.dataset.productId;
        if (!pid || seen.has(pid)) return;
        seen.add(pid);
        subscriptionHtmlItems.push(html);
      });

      if (subscriptionHtmlItems.length > 0) {
        this.renderSubscriptionItems(subscriptionHtmlItems);
      } else if (!hasRecommendations) {
        this.classList.add("hidden");
        this.destroySlider();
        if (this.list) this.list.innerHTML = "";
        return;
      }

      if (!hasRecommendations) return;

      const recommendationItems = await this.fetchRecommendationItems(
        productIds,
        cartIds,
        seen,
        loadId,
      );
      if (loadId !== this._loadId) return;

      if (!recommendationItems.length) {
        if (!subscriptionHtmlItems.length) {
          this.classList.add("hidden");
          this.destroySlider();
          if (this.list) this.list.innerHTML = "";
        }
        return;
      }

      if (subscriptionHtmlItems.length > 0) {
        this.list.insertAdjacentHTML("beforeend", recommendationItems.join(""));
        this.initSlider();
        return;
      }

      this.renderSubscriptionItems(recommendationItems);
    }
  }

  customElements.define("oo-cart-upsell", OoCartUpsell);
}
